import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Route, withRouter, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import Layout from '../../Layout';

import { changeDate } from '../store/actions';
import { handleLogout, setSingleEstablishment, setInitialEstablishments, setAppPermissionEstablishments } from '../../../pages/Login/store/actions';
import { setEstablishmentId } from '../../../pages/BusinessInfo/store/actions';

import { formatEstablishments } from '../../../helpers/formatters';
import { SITE_RESTRICTIONS_TO_PAGES } from 'helpers/restrictions';

import { setToken } from '../../../services';

const currentDate = moment().format('YYYY-MM-DD');
const isBeforeCurrentDate = date => moment(currentDate).isAfter(date, 'day');

const LayoutRoute = ({ component: Component, singleEstablishment, path, ...rest }) => {
  const dispatch = useDispatch();

  const history = useHistory();

  const { userRestrictions, submit: { isAdminUser } } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { endDate } = useSelector(state => state.dateFilter);
  const { token, loggedIn, tokenExpirationTime, establishments } = useSelector(state => state.login);
  const situationEstablishments = useSelector(state => state.login.situation.establishments);

  const [isSetupComplete, setIsSetupComplete] = useState(false);
  const [isLoginFromNoAccessCode] = useState(!!history.location.state?.data?.noAccessCode);

  const filterEstablishments = formatEstablishments(establishments, +isAdminUser == 1);

  const premiumPackageEstablishments = situationEstablishments
    ?.filter(establishment => +establishment?.premiumPackage == 1)
    ?.map(establishment => establishment?.establishmentId);

  const isPremiumPackage = premiumPackageEstablishments?.includes(establishmentId);

  const isSelfService = history.location.pathname == '/autoatendimento';
  const isPostSaleReminder = ['/lembrete-pos-venda', '/configuracao-de-lembrete'].includes(history.location.pathname);

  const selfServiceEstablishmentsId = situationEstablishments
    ?.filter(establishment => establishment?.establishmentTypes?.map(Number)?.includes(1))
    ?.map(establishment => establishment?.establishmentId);

  const postSaleEstablishmentsId = situationEstablishments
    ?.filter(establishment => establishment?.establishmentTypes?.map(Number)?.includes(2))
    ?.map(establishment => establishment?.establishmentId);

  let routeEstablishments = filterEstablishments;
  console.log(routeEstablishments);

  if(isSelfService) {
    routeEstablishments = filterEstablishments?.filter(establishment => selfServiceEstablishmentsId?.includes(establishment?.value));
  }

  if(isPostSaleReminder) {
    routeEstablishments = filterEstablishments?.filter(establishment => postSaleEstablishmentsId?.includes(establishment?.value));
  }

  const availableEstablishmentsId = establishments
    ?.map(establishment => establishment?.establishmentId);

  const blockedRoutes = SITE_RESTRICTIONS_TO_PAGES
    ?.filter(restriction => userRestrictions?.includes(restriction?.restriction))
    ?.map(restriction => restriction?.page);

  const verifyTokenExpired = () => {
    return !!(tokenExpirationTime && moment().isAfter(tokenExpirationTime));
  }

  const redirectToLogin = () => {
    dispatch(handleLogout());
    return () => history.push('/');
  }

  useEffect(() => {
    if(+isAdminUser == 1) {
      if(!isPremiumPackage) {
        const warningRoutes = [
          '/produtos',
          '/produtos-venda',
          '/produtos-consumo',
          '/notas-fiscais',
          '/funcionarios',
          '/novo-funcionario',
          '/editar-funcionario',
          '/comissionamento',
          '/agendamento',
          '/novo-agendamento',
          '/novo-agendamento-servico',
          '/configuracao-agendamento'
        ];

        if(warningRoutes.includes(history.location.pathname)) {
          toast.warn('Estabelecimento sem Pacote Premium');
        }
      }
    }
  }, [history.location.pathname]);

  useEffect(() => {
    (async () => {
      if(+isAdminUser != 1) {
        if(blockedRoutes?.includes(history.location.pathname)) {
          toast.error('Você não tem autorização para acessar esta área.');
          return history.push('/visao-geral');
        }

        const setValidRoutes = async () => {
          const premiumPackageRoutes = ['/funcionarios', '/comissionamento', '/comissoes', '/agendamento', '/notas-fiscais', '/configuracoes-de-notas-fiscais'];

          let establishments = situationEstablishments;

          if(premiumPackageRoutes?.includes(history.location.pathname)) {
            establishments = situationEstablishments?.filter(establishment => +establishment?.premiumPackage == 1);
          }

          return establishments;
        }

        const establishments = await setValidRoutes();
        dispatch(setAppPermissionEstablishments(establishments, isAdminUser));
      }
    })();
  }, [history.location.pathname]);

  useEffect(() => {
    const availableEstablishmentsIdWithEmptyEstablishment = availableEstablishmentsId?.includes('')
      ? availableEstablishmentsId
      : [...availableEstablishmentsId, ''];

    if(!availableEstablishmentsIdWithEmptyEstablishment?.includes(establishmentId) && !singleEstablishment) {
      dispatch(setEstablishmentId({ establishmentId: '' }));
    }

    if(!availableEstablishmentsIdWithEmptyEstablishment?.includes(establishmentId) && singleEstablishment) {
      dispatch(setEstablishmentId({ establishmentId: availableEstablishmentsId[0] }));
    }
  }, [availableEstablishmentsId]);

  useEffect(() => {
    if((isSelfService || isPostSaleReminder) && routeEstablishments?.length > 0) {
      const isEstablishmentAvailable = routeEstablishments?.some(establishment => establishment?.value == establishmentId);

      if(!isEstablishmentAvailable) {
        dispatch(setEstablishmentId({ establishmentId: routeEstablishments[0]?.value }));
      }
    }
  }, [isSelfService, isPostSaleReminder, establishmentId, routeEstablishments?.length]);

  useEffect(() => {
    if(isLoginFromNoAccessCode) {
      const accessToken = token || history.location.state?.data?.token;

      if(accessToken) {
        setToken(accessToken);
      }

      setIsSetupComplete(true);
    }
  }, [isLoginFromNoAccessCode, token]);

  useEffect(() => {
    if(history.location.pathname != '/suas-assinaturas') {
      if(isLoginFromNoAccessCode) {
        dispatch(handleLogout());
        toast.error('Ação não autorizada.');
        return history.push('/');
      }
    }
  }, [isLoginFromNoAccessCode, history.location.pathname]);

  useEffect(() => {
    if(!isLoginFromNoAccessCode) {
      if(isBeforeCurrentDate(endDate)) {
        dispatch(changeDate(currentDate, currentDate));
      }

      if(
        filterEstablishments.length === 0 ||
        (filterEstablishments.length === 1 && filterEstablishments[0].establishmentName === 'Todos os estabelecimentos')
      ) {
        history.push('/');
      }
    }
  }, []);

  useEffect(() => {
    if(!isLoginFromNoAccessCode) {
      if(!loggedIn) {
        return redirectToLogin();
      }

      if(verifyTokenExpired()) {
        return redirectToLogin();
      }

      if(token) {
        setToken(token);
      }

      setIsSetupComplete(true);
    }
  }, [loggedIn, verifyTokenExpired()]);

  useEffect(() => {
    if(!isLoginFromNoAccessCode) {
      const filterAllEstablishmentsOption = filterEstablishments.filter(e => !e.value);
      const filterRestEstablishments = filterEstablishments.filter(e => e.value);

      if(
        singleEstablishment &&
        !establishmentId &&
        filterRestEstablishments.length > 0 &&
        filterAllEstablishmentsOption.length === 0
      ) {
        dispatch(setSingleEstablishment(filterRestEstablishments));

        dispatch(
          setEstablishmentId({
            establishmentId: filterRestEstablishments[0].value,
          })
        );
      }

      if(singleEstablishment && filterAllEstablishmentsOption.length > 0) {
        const firstEstablishmentId = establishmentId || filterEstablishments[1]?.value;

        dispatch(setSingleEstablishment(filterEstablishments));
        dispatch(setEstablishmentId({ establishmentId: firstEstablishmentId }));
      }

      if(
        !singleEstablishment &&
        filterEstablishments.length > 1 &&
        filterAllEstablishmentsOption.length === 0
      ) {
        dispatch(setInitialEstablishments(filterEstablishments));
      }
    }
  }, [filterEstablishments, establishmentId, singleEstablishment]);

  return(
    <Layout filterEstablishments={routeEstablishments} {...rest}>
      {isSetupComplete && (
        <Route
          path={path}
          render={matchProps => <Component {...matchProps} />}
        />
      )}
    </Layout>
  );
}

LayoutRoute.defaultProps = {
  singleEstablishment: false
};

LayoutRoute.propTypes = {
  singleEstablishment: PropTypes.bool,
  component: PropTypes.element.isRequired,
  path: PropTypes.string.isRequired
};

export default withRouter(LayoutRoute);